export const DEFAULT_FILTERS = {
  adherentId: "",
  groupId: "",
};

export const RANK_KEYS = [
  "1èr(e)",
  "2ème",
  "3ème",
  "4ème",
  "5ème",
  "7ème",
  "9ème",
  "N.C.",
];

export const COMBAT_COLORS = {
  wins: "#22C55E",
  losses: "#EF4444",
  draws: "#94A3B8",
};

export const RANK_COLORS = {
  "1èr(e)": "#F59E0B",
  "2ème": "#64748B",
  "3ème": "#B45309",
  "4ème": "#38BDF8",
  "5ème": "#2563EB",
  "7ème": "#4F46E5",
  "9ème": "#7C3AED",
  "N.C.": "#CBD5E1",
};

export function getUserId(user) {
  return user?.userId || user?.id || null;
}

export function getAdherentName(adherent) {
  return (
    [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "Judoka"
  );
}

export function getGroupNames(adherent) {
  if (Array.isArray(adherent?.groups) && adherent.groups.length) {
    return adherent.groups.map((group) => group.nom).filter(Boolean).join(", ");
  }

  return adherent?.groupName || "Sans groupe";
}

export function mergeApprovedAdherentsById(adherents = []) {
  const map = new Map();

  adherents.forEach((adherent) => {
    if (!adherent?.id) return;

    const existing = map.get(adherent.id);

    if (!existing) {
      map.set(adherent.id, {
        ...adherent,
        groups: adherent.group ? [adherent.group] : [],
      });
      return;
    }

    const hasGroup = existing.groups.some(
      (group) => Number(group.id) === Number(adherent.group?.id),
    );

    map.set(adherent.id, {
      ...existing,
      ...adherent,
      groups:
        adherent.group && !hasGroup
          ? [...existing.groups, adherent.group]
          : existing.groups,
    });
  });

  return Array.from(map.values()).sort((a, b) =>
    getAdherentName(a).localeCompare(getAdherentName(b), "fr"),
  );
}

export function parseCombatResults(value = "") {
  return Array.from(String(value || "")).reduce(
    (acc, token) => {
      if (token === "✅") acc.wins += 1;
      else if (token === "❌") acc.losses += 1;
      else if (token === "F") acc.losses += 1;
      else if (token === "D") acc.losses += 1;
      else if (token === "N") acc.draws += 1;

      return acc;
    },
    {
      wins: 0,
      losses: 0,
      draws: 0,
    },
  );
}

export function getEmptyClassements() {
  return RANK_KEYS.reduce((acc, rank) => {
    acc[rank] = 0;
    return acc;
  }, {});
}

export function flattenEventRows(adherents = [], eventsByAdherent = {}) {
  return adherents.flatMap((adherent) => {
    const events = eventsByAdherent[adherent.id] || [];

    return events.map((event) => ({
      adherent,
      event,
      adherentId: adherent.id,
    }));
  });
}

export function filterAdherents(adherents = [], filters = {}) {
  return adherents.filter((adherent) => {
    const matchesAdherent =
      !filters.adherentId ||
      String(adherent.id) === String(filters.adherentId);

    const matchesGroup =
      !filters.groupId ||
      adherent.groups?.some(
        (group) => String(group.id) === String(filters.groupId),
      );

    return matchesAdherent && matchesGroup;
  });
}

export function buildGlobalStats(rows = [], filteredAdherents = []) {
  const today = new Date();
  const classements = getEmptyClassements();

  let competitionsAVenir = 0;
  let competitionsEffectuees = 0;
  let totalCombats = 0;
  let wins = 0;
  let losses = 0;
  let draws = 0;
  let podiums = 0;

  rows.forEach(({ event }) => {
    const eventDate = new Date(event?.start);

    if (!Number.isNaN(eventDate.getTime()) && eventDate > today) {
      competitionsAVenir += 1;
    } else {
      competitionsEffectuees += 1;
    }

    const combatStats = parseCombatResults(event?.AdherentEvent?.resultats);

    wins += combatStats.wins;
    losses += combatStats.losses;
    draws += combatStats.draws;
    totalCombats += combatStats.wins + combatStats.losses + combatStats.draws;

    const classement = event?.AdherentEvent?.classement || "N.C.";

    if (classements[classement] !== undefined) {
      classements[classement] += 1;
    } else {
      classements["N.C."] += 1;
    }

    if (["1èr(e)", "2ème", "3ème"].includes(classement)) {
      podiums += 1;
    }
  });

  const victoryRate = totalCombats
    ? Math.round((wins / totalCombats) * 100)
    : 0;

  return {
    judokas: filteredAdherents.length,
    participations: rows.length,
    competitionsEffectuees,
    competitionsAVenir,
    totalCombats,
    wins,
    losses,
    draws,
    podiums,
    victoryRate,
    classements,
  };
}

export function buildJudokaSummaries(filteredAdherents = [], rows = []) {
  const map = new Map();

  filteredAdherents.forEach((adherent) => {
    map.set(adherent.id, {
      adherent,
      participations: 0,
      totalCombats: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      podiums: 0,
      victoryRate: 0,
    });
  });

  rows.forEach(({ adherent, event }) => {
    const current = map.get(adherent.id);

    if (!current) return;

    const combatStats = parseCombatResults(event?.AdherentEvent?.resultats);
    const combats = combatStats.wins + combatStats.losses + combatStats.draws;
    const classement = event?.AdherentEvent?.classement || "";

    current.participations += 1;
    current.totalCombats += combats;
    current.wins += combatStats.wins;
    current.losses += combatStats.losses;
    current.draws += combatStats.draws;

    if (["1èr(e)", "2ème", "3ème"].includes(classement)) {
      current.podiums += 1;
    }
  });

  return Array.from(map.values())
    .map((summary) => ({
      ...summary,
      victoryRate: summary.totalCombats
        ? Math.round((summary.wins / summary.totalCombats) * 100)
        : 0,
    }))
    .sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.podiums !== a.podiums) return b.podiums - a.podiums;
      return b.participations - a.participations;
    });
}

export function getCombatChartData(stats) {
  return [
    {
      name: "Victoires",
      value: stats.wins || 0,
      color: COMBAT_COLORS.wins,
    },
    {
      name: "Défaites",
      value: stats.losses || 0,
      color: COMBAT_COLORS.losses,
    },
    {
      name: "Nuls",
      value: stats.draws || 0,
      color: COMBAT_COLORS.draws,
    },
  ];
}

export function getRankChartData(classements = {}) {
  return RANK_KEYS.map((rank) => ({
    name: rank,
    value: classements[rank] || 0,
    color: RANK_COLORS[rank] || "#CBD5E1",
  })).filter((item) => item.value > 0);
}